import { cycleKey, cycleLabel, isCycleWritable } from '@/modules/cycles/cycle-state';
import type { Cycle } from '@/modules/cycles/types';
import type { Task, TaskStatus } from '@/modules/tasks/types';
import { isCalendarDate } from '@/shared/lib/date-utils';

export const TASK_TITLE_MAX_LENGTH = 500;

export const TASK_STATUSES: readonly TaskStatus[] = ['open', 'done', 'discussed'];

export interface NewTaskInput {
  id: string;
  workspaceId: string;
  teamId: string;
  title: string;
  ownerId?: string | null;
  dueDate?: string | null;
  status?: TaskStatus;
  completedAt?: string | null;
  issueId?: string | null;
  milestoneId?: string | null;
  rockId?: string | null;
  delayCount?: number;
  year?: number | null;
  quarter?: number | null;
}

export interface AssignablePerson {
  id: string;
  workspaceId: string;
  archived: boolean;
  hasAccount: boolean;
  onHomeTeam: boolean;
}

export interface NewTaskFacts {
  cycles: Cycle[];
  canManageCycles: boolean;
  unlockedCycleKey: string | null;
  people: AssignablePerson[];
}

export type TaskRuleCode =
  | 'title_required'
  | 'title_too_long'
  | 'due_date_invalid'
  | 'cycle_not_writable'
  | 'assignee_not_assignable';

export interface TaskRuleProblem {
  code: TaskRuleCode;
  message: string;
}

export function taskTitleProblem(title: string): TaskRuleProblem | null {
  const trimmed = title.trim();
  if (!trimmed) return { code: 'title_required', message: 'A title is required.' };
  if (trimmed.length > TASK_TITLE_MAX_LENGTH) {
    return {
      code: 'title_too_long',
      message: `The title must be at most ${TASK_TITLE_MAX_LENGTH} characters.`,
    };
  }
  return null;
}

export function taskCycleProblem(
  year: number | null | undefined,
  quarter: number | null | undefined,
  facts: Pick<NewTaskFacts, 'cycles' | 'canManageCycles' | 'unlockedCycleKey'>,
): TaskRuleProblem | null {
  const writable = isCycleWritable({
    cycles: facts.cycles,
    year,
    quarter,
    canManage: facts.canManageCycles,
    unlockedKey: facts.unlockedCycleKey,
  });
  if (writable) return null;
  const state = facts.cycles.find((c) => year != null && cycleKey(c.year, c.quarter) === cycleKey(year, quarter))?.state;
  return {
    code: 'cycle_not_writable',
    message: state === 'planning'
      ? `${cycleLabel(year, quarter)} is still being planned.`
      : `${cycleLabel(year, quarter)} is closed.`,
  };
}

export function taskAssigneeProblem(
  ownerId: string | null | undefined,
  workspaceId: string,
  people: AssignablePerson[],
): TaskRuleProblem | null {
  if (!ownerId) return null;
  const person = people.find((candidate) => candidate.id === ownerId);
  if (!person || person.workspaceId !== workspaceId || person.archived) {
    return {
      code: 'assignee_not_assignable',
      message: 'That person cannot be assigned work in this workspace.',
    };
  }
  return null;
}

export function taskOwnerOnHomeTeam(
  ownerId: string | null | undefined,
  people: AssignablePerson[],
): string | null {
  if (!ownerId) return null;
  const person = people.find((candidate) => candidate.id === ownerId);
  return person?.hasAccount && !person.onHomeTeam ? null : ownerId;
}

export function newTaskProblem(input: NewTaskInput, facts: NewTaskFacts): TaskRuleProblem | null {
  const title = taskTitleProblem(input.title);
  if (title) return title;
  if (input.dueDate && !isCalendarDate(input.dueDate)) {
    return { code: 'due_date_invalid', message: 'The due date must be a real date written as YYYY-MM-DD.' };
  }
  return taskCycleProblem(input.year, input.quarter, facts)
    ?? taskAssigneeProblem(input.ownerId, input.workspaceId, facts.people);
}

export function newTaskRow(input: NewTaskInput, now: string): Record<string, unknown> {
  const status = input.status ?? 'open';
  return {
    id: input.id,
    workspace_id: input.workspaceId,
    team_id: input.teamId,
    title: input.title.trim(),
    owner_id: input.ownerId ?? null,
    due_date: input.dueDate ?? null,
    status,
    completed: status === 'done',
    completed_at: status === 'done' ? (input.completedAt ?? now) : null,
    issue_id: input.issueId ?? null,
    milestone_id: input.milestoneId ?? null,
    rock_id: input.rockId ?? null,
    delay_count: input.delayCount ?? 0,
    year: input.year ?? null,
    quarter: input.quarter ?? null,
  };
}

export const TASK_AUDITED_FIELDS: (keyof Task)[] = ['title', 'ownerId', 'dueDate', 'completed', 'issueId', 'rockId'];

export function normalizeTaskPatch(updates: Partial<Task>): Partial<Task> {
  const patch: Partial<Task> = { ...updates };
  if (patch.status !== undefined && patch.completed === undefined) {
    patch.completed = patch.status === 'done';
  }
  if (patch.completed !== undefined && patch.status === undefined) {
    patch.status = patch.completed ? 'done' : 'open';
  }
  if (patch.completedAt === undefined) {
    patch.completedAt = patch.completed ? new Date().toISOString() : undefined;
  }
  return patch;
}

export function taskDbUpdates(patch: Partial<Task>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (patch.title !== undefined) db.title = patch.title;
  if (patch.ownerId !== undefined) db.owner_id = patch.ownerId;
  if (patch.dueDate !== undefined) db.due_date = patch.dueDate;
  if (patch.completed !== undefined) db.completed = patch.completed;
  if (patch.completedAt !== undefined) db.completed_at = patch.completedAt ?? null;
  if (patch.status !== undefined) db.status = patch.status;
  if (patch.queuedAction !== undefined) db.queued_action = patch.queuedAction ?? null;
  if (patch.issueId !== undefined) db.issue_id = patch.issueId;
  if (patch.delayCount !== undefined) db.delay_count = patch.delayCount;
  if (patch.year !== undefined) db.year = patch.year ?? null;
  if (patch.quarter !== undefined) db.quarter = patch.quarter ?? null;
  if (patch.rockId !== undefined) db.rock_id = patch.rockId ?? null;
  return db;
}
